import {format, fromUnixTime, parseISO, differenceInDays} from 'date-fns'
import {toast} from 'react-toastify'
import {toAbsoluteUrl} from '../../_metronic/helpers'
import {supportedFileExtensions, viewableFileTypes} from './_constants'
import brandingConfig from '../../config/brandingConfig'

export const copyToClipboard = (
  textToCopy: string,
  alertToast: boolean = false,
  toastMessage: string = ''
) => {
  // navigator.clipboard.writeText(textToCopy)
  navigator.clipboard.writeText(textToCopy)
  if (alertToast && toastMessage) {
    toast.success(toastMessage)
    // toast.success(toastMessage)
  }
}

export const serialize = function (obj: any) {
  let str = []
  for (let p in obj)
    if (obj.hasOwnProperty(p) && !!obj[p]) {
      str.push(encodeURIComponent(p) + '=' + encodeURIComponent(obj[p]))
    }
  return str.join('&')
}

export function parseJsonString(jsonString: string) {
  try {
    if (!jsonString || typeof jsonString !== "string" || jsonString.trim() === "") {
      return null;
    }
    return JSON.parse(jsonString)
  } catch (error) {
    return null // null indicates parsing error
  }
}

export const getTodayDate = () => {
  const today = new Date()
  const yyyy = today.getFullYear()
  const mm = String(today.getMonth() + 1).padStart(2, '0') // January is 0!
  const dd = String(today.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

export const formatDate = (dateInput = new Date()) => {
  const date = new Date(dateInput)
  const yyyy = date.getFullYear()
  const mm = String(date.getMonth() + 1).padStart(2, '0') // January is 0!
  const dd = String(date.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

/*
  Converts a string from various programming naming conventions such as camelCase, PascalCase, kebab-case, and 
  snake_case to a more readable Title Case format. This function is particularly useful for formatting 
  internal string representations into a format suitable for user interfaces or documentation.
*/
export function toTitleCase(input: string) {
  // First, handle camelCase and PascalCase by adding a space before each uppercase letter (if it's not at the start).
  const withSpaces = input.replace(/([A-Z])/g, (match, p1, offset) => (offset > 0 ? ' ' + p1 : p1))

  // Next, replace any underscores or hyphens with spaces, making sure to handle multiple separators together.
  const noUnderscoresOrHyphens = withSpaces.replace(/[_-]+/g, ' ')

  // Finally, capitalize the first letter of each word and make the rest lowercase.
  const titleCased = noUnderscoresOrHyphens
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')

  return titleCased
}

/**
 * Sanitizes a string to be safe for use as a filename in AWS S3 by replacing spaces with dashes, collapsing consecutive spaces,
 * and removing or replacing other unsafe characters.
 *
 * EXAMPLE:  "user's   profile  picture@2024.png" => user_s-profile-picture_2024.png
 *
 * @param input The original string to be sanitized.
 * @returns A string safe for use as a file name, where only alphanumeric characters, dashes, underscores, and periods are allowed,
 * and spaces are replaced with a single dash.
 */
export function sanitizeFileName(input: string): string {
  // First, replace consecutive spaces with a single space to ensure collapsing into a single dash later
  let normalizedSpaces = input.replace(/\s+/g, ' ')
  // Replace single space with a dash
  normalizedSpaces = normalizedSpaces.replace(/ /g, '-')
  // Allow only alphanumeric characters, dashes, underscores, and periods; replace all other characters with an underscore
  const safeFileName = normalizedSpaces.replace(/[^a-zA-Z0-9-_.]/g, '_')
  return safeFileName
}

/**
 * Returns a human-readable billing period string based on numeric values and their corresponding units.
 *
 * @param billingPeriod - The numeric duration of the period (1, 2, etc.)
 * @param billingPeriodUnit - The unit of time for the billing period ("month", "day", "week", etc.)
 * @returns {string} A human-readable string describing the billing period.
 */
export function formatBillingPeriod(billingPeriod: number, billingPeriodUnit: string): string {
  switch (billingPeriodUnit?.toLowerCase()) {
    case 'day':
      if (billingPeriod === 1) {
        return 'Daily'
      } else {
        return `${billingPeriod} Days`
      }
    case 'week':
      if (billingPeriod === 1) {
        return 'Weekly'
      } else {
        return `${billingPeriod} Weeks`
      }
    case 'month':
      if (billingPeriod === 1) {
        return 'Monthly'
      } else if (billingPeriod === 3) {
        return 'Quarterly'
      } else if (billingPeriod === 6) {
        return 'Half Yearly'
      } else {
        return `${billingPeriod} Months`
      }
    case 'year':
      if (billingPeriod === 1) {
        return 'Yearly'
      } else {
        return `${billingPeriod} Years`
      }
    default:
      return 'Unknown Billing Period'
  }
}

/**
 * Formats a date given either as a Unix timestamp in seconds or as an ISO standard date string.
 * If parsing fails, the input string is returned as is. The output format can be specified,
 * defaulting to "MMM dd, yyyy" if not provided.
 *
 * @param {number|string} dateInput - The date input which can be a Unix timestamp in seconds or an ISO date string.
 * @param {string} [outputFormat="dd MMM, yyyy"] - Optional. The format string for the output date.
 * @returns {string} The formatted date string in the specified format, or the original input if parsing fails.
 */
export const getFormattedDate = (dateInput: number | string, outputFormat = brandingConfig.invoiceDefaultDateFormate,
) => {
  let date

  if (typeof dateInput === 'number') {
    // Assume it's a Unix timestamp in seconds
    date = fromUnixTime(dateInput)
  } else if (typeof dateInput === 'string') {
    try {
      // Attempt to parse the string assuming it's in ISO format
      date = parseISO(dateInput)
      if (isNaN(date.getTime())) {
        // Check if date is valid
        return dateInput // Return original input if parsing fails
      }
    } catch (e) {
      return dateInput // Return original input if an error occurs
    }
  } else {
    return 'Invalid input' // Return error message if input type is incorrect
  }

  return format(date, outputFormat)
}

/**
 * Calculates the number of full days between a given Unix timestamp and the current time.
 *
 * @param {number} unixTimestamp - The Unix timestamp in seconds.
 * @returns {number} The number of full days between the given timestamp and now.
 */
export const getDaysDifference = (unixTimestamp: number) => {
  const dateFromTimestamp = fromUnixTime(unixTimestamp)
  const now = new Date()
  return differenceInDays(now, dateFromTimestamp)
}

export const getFileType = (fileName: string) => {
  const extension = fileName?.split('.')?.pop()?.toLowerCase()
  if(supportedFileExtensions.includes(extension!)){
    return toAbsoluteUrl(`/media/icons/file_extensions/${extension}.svg`)
  } else {
    return toAbsoluteUrl(`/media/icons/file_extensions/file.svg`)
  }

 
}

export function parseBoolean(str: string) {
  return str === 'true'
}

export function parseCurrency(value: number): string {
  if (value == null || isNaN(value)) {
    return ''
  }

  value = parseInt(value.toString(), 10)

  if (value >= 1_000_000_000_000) {
    return (value / 1_000_000_000_000).toFixed(1).replace(/\.0$/, '') + 'T'
  } else if (value >= 1_000_000_000) {
    return (value / 1_000_000_000).toFixed(1).replace(/\.0$/, '') + 'B'
  } else if (value >= 1_000_000) {
    return (value / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M'
  } else if (value >= 1_000) {
    return (value / 1_000).toFixed(1).replace(/\.0$/, '') + 'K'
  } else {
    return value.toString()
  }
}

export function formatCurrency(value: number, currency: string = 'INR'): string {
  const checkValue = Number(value)
  const formatter = new Intl.NumberFormat(brandingConfig.defaultLocale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0, // You can adjust this if you need cents
    maximumFractionDigits: 0,
  })

  return formatter.format(checkValue)
}

export function formatNumber(value: number): string {
  const checkValue = Number(value)

  return checkValue
    .toFixed(2)
    .replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    .replace(/\.00$/, '')
}

export function getCurrencySymbol(currencyBill: string, currencyOptions: any[]): string {
  const currency = currencyOptions.find((option: any) => option.name.startsWith(currencyBill))

  if (currency) {
    const symbolMatch = currency.name.match(/\(([^)]+)\)/)
    return symbolMatch ? symbolMatch[1] : ''
  }

  return ''
}

export function viewableFile(fileName: string) {
  const fileType = fileName.split('.').pop()
  return viewableFileTypes.includes(fileType!)
}

export const isOlderThan18 = (dateOfBirth: string) => {
  const currentDate = new Date()
  const dob = new Date(dateOfBirth)

  // Calculate the difference in years
  let age = currentDate.getFullYear() - dob.getFullYear()
  const monthDiff = currentDate.getMonth() - dob.getMonth()
  const dayDiff = currentDate.getDate() - dob.getDate()

  // Adjust for month and day difference
  if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
    age -= 1
  }

  // Return true if age is 18 or older
  return age >= 18
}

export const formatTransactionId = (transactionId: string): string => {
  if (!transactionId || transactionId === '-') {
    return '-';
  }
  return `${transactionId.slice(0, 5)}...${transactionId.slice(-4)}`;
};

export const formatFileSize = (bytes: number | string): string => {
  // Convert string to number if necessary
  const size = typeof bytes === 'string' ? parseInt(bytes) : bytes;
  
  // Handle invalid or zero size
  if (!size || size === 0) return '0 B';

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const base = 1024;
  const decimals = 2;

  // Calculate the appropriate unit
  const unitIndex = Math.floor(Math.log(size) / Math.log(base));

  // Ensure we don't exceed available units
  const normalizedUnit = Math.min(unitIndex, units.length - 1);
  
  // Calculate the final value
  const value = size / Math.pow(base, normalizedUnit);

  // Format with appropriate decimals
  // Remove trailing zeros and decimal point if no decimals
  const formatted = value.toFixed(decimals).replace(/\.?0+$/, '');
  
  return `${formatted} ${units[normalizedUnit]}`;
};

export const formatDateString = (dateString?: string) => {
  if (!dateString) return "-"; // Handle undefined or null case

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "Invalid Date"; // Handle invalid date

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
};

export const removeNullFields = (obj: Record<string, any>): Record<string, any> => {
  return Object.fromEntries(
    Object.entries(obj)
      .filter(([_, v]) => v !== null && v !== undefined) // Remove null & undefined
      .map(([k, v]) => [k, v instanceof Object && !Array.isArray(v) ? removeNullFields(v) : v]) // Recursively process objects
  )
}

// logger.ts
type ConsoleLogParams = Parameters<typeof console.log>;

export function consoleLog(...args: ConsoleLogParams): void {
  if (process.env.REACT_APP_ENV === 'dev') {
    console.log(...args);
  }
}

export const convertToBytes = (value: number, type: string): number => {
  const size = Number(value);
  if (isNaN(size)) return 0;

  switch (type) {
    case 'KB':
      return size * 1024;
    case 'MB':
      return size * 1024 * 1024;
    case 'GB':
      return size * 1024 * 1024 * 1024;
    default:
      return size;
  }
};
